import React, { useState } from "react";
import {
  Box,
  Drawer,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import HeightIcon from "@mui/icons-material/Height";

import SchoolIcon from "@mui/icons-material/School";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import LocationFilters from "../../components/LocationFilters";

// Helper function to format height
const formatHeight = (inches) => {
  const feet = Math.floor(inches / 12);
  const remainingInches = inches % 12;
  return `${feet}'${remainingInches}"`;
};

const AdvancedFilterPanel = ({
  open,
  filters,
  onFilterChange,
  onClearAllFilters,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // State for temporary filter values
  const [tempFilters, setTempFilters] = useState(filters);

  // Handle filter change
  const handleFilterChange = (category, field, value) => {
    setTempFilters((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value,
      },
    }));
  };

  // Handle slider change
  const handleSliderChange =
    (category, minField, maxField) => (event, newValue) => {
      setTempFilters((prev) => ({
        ...prev,
        [category]: {
          ...prev[category],
          [minField]: newValue[0],
          [maxField]: newValue[1],
        },
      }));
    };

  // Handle min input change
  const handleMinInputChange =
    (category, minField, maxField, min, max) => (event) => {
      const inputValue = event.target.value;
      if (inputValue === "") return;

      const value = Number(inputValue);
      if (!isNaN(value)) {
        const clampedValue = Math.max(min, Math.min(max, value));
        handleFilterChange(category, minField, clampedValue);
      }
    };

  // Handle max input change
  const handleMaxInputChange =
    (category, minField, maxField, min, max) => (event) => {
      const inputValue = event.target.value;
      if (inputValue === "") return;

      const value = Number(inputValue);
      if (!isNaN(value)) {
        const clampedValue = Math.max(min, Math.min(max, value));
        handleFilterChange(category, maxField, clampedValue);
      }
    };

  // Handle apply filters
  const handleApplyFilters = () => {
    // Update all filters at once
    Object.keys(tempFilters).forEach((category) => {
      onFilterChange(category, tempFilters[category]);
    });
  };

  return (
    <Drawer
      variant={isMobile ? "temporary" : "persistent"}
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        width: 300,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: 300,
          boxSizing: "border-box",
          position: isMobile ? "fixed" : "relative",
          height: isMobile ? "100%" : "auto",
          mt: isMobile ? 0 : 2,
          borderRadius: isMobile ? 0 : 2,
          border: isMobile ? "none" : `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" component="h2">
          Advanced Filters
        </Typography>
        {isMobile && (
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <Divider />

      <Box sx={{ p: 2, overflowY: "auto" }}>
        {/* Physical Attributes */}
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="physical-content"
            id="physical-header"
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <HeightIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
              <Typography>Physical Attributes</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="body2">Height</Typography>
                <Button
                  size="small"
                  startIcon={<RestartAltIcon />}
                  onClick={() => {
                    handleFilterChange("physical", "heightMin", 60);
                    handleFilterChange("physical", "heightMax", 84);
                  }}
                >
                  Reset
                </Button>
              </Box>
              <Slider
                value={[
                  tempFilters.physical.heightMin,
                  tempFilters.physical.heightMax,
                ]}
                onChange={handleSliderChange(
                  "physical",
                  "heightMin",
                  "heightMax"
                )}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => formatHeight(value)}
                min={60}
                max={84}
                marks={[
                  { value: 60, label: "5'0\"" },
                  { value: 72, label: "6'0\"" },
                  { value: 84, label: "7'0\"" },
                ]}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="body2">Weight (lbs)</Typography>
                <Button
                  size="small"
                  startIcon={<RestartAltIcon />}
                  onClick={() => {
                    handleFilterChange("physical", "weightMin", 100);
                    handleFilterChange("physical", "weightMax", 350);
                  }}
                >
                  Reset
                </Button>
              </Box>
              <Slider
                value={[
                  tempFilters.physical.weightMin,
                  tempFilters.physical.weightMax,
                ]}
                onChange={handleSliderChange(
                  "physical",
                  "weightMin",
                  "weightMax"
                )}
                valueLabelDisplay="auto"
                min={100}
                max={350}
                marks={[
                  { value: 100, label: "100" },
                  { value: 225, label: "225" },
                  { value: 350, label: "350" },
                ]}
              />
            </Box>

            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="body2">Vertical Jump (inches)</Typography>
                <Button
                  size="small"
                  startIcon={<RestartAltIcon />}
                  onClick={() => {
                    handleFilterChange("physical", "verticalJumpMin", 0);
                    handleFilterChange("physical", "verticalJumpMax", 50);
                  }}
                >
                  Reset
                </Button>
              </Box>
              <Slider
                value={[
                  tempFilters.physical.verticalJumpMin,
                  tempFilters.physical.verticalJumpMax,
                ]}
                onChange={handleSliderChange(
                  "physical",
                  "verticalJumpMin",
                  "verticalJumpMax"
                )}
                valueLabelDisplay="auto"
                min={0}
                max={50}
                marks={[
                  { value: 0, label: '0"' },
                  { value: 25, label: '25"' },
                  { value: 50, label: '50"' },
                ]}
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Academic Performance */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="academic-content"
            id="academic-header"
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <SchoolIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
              <Typography>Academic Performance</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="body2">GPA</Typography>
                <Button
                  size="small"
                  startIcon={<RestartAltIcon />}
                  onClick={() => {
                    handleFilterChange("academic", "gpaMin", 2.0);
                    handleFilterChange("academic", "gpaMax", 4.0);
                  }}
                >
                  Reset
                </Button>
              </Box>
              <Slider
                value={[
                  tempFilters.academic.gpaMin,
                  tempFilters.academic.gpaMax,
                ]}
                onChange={handleSliderChange("academic", "gpaMin", "gpaMax")}
                valueLabelDisplay="auto"
                min={2.0}
                max={4.0}
                step={0.1}
                marks={[
                  { value: 2.0, label: "2.0" },
                  { value: 3.0, label: "3.0" },
                  { value: 4.0, label: "4.0" },
                ]}
              />
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}
              >
                <TextField
                  label="Min"
                  value={tempFilters.academic.gpaMin}
                  onChange={handleMinInputChange(
                    "academic",
                    "gpaMin",
                    "gpaMax",
                    2.0,
                    4.0
                  )}
                  type="number"
                  size="small"
                  sx={{ width: "45%" }}
                  InputProps={{ inputProps: { min: 2.0, max: 4.0, step: 0.1 } }}
                />
                <TextField
                  label="Max"
                  value={tempFilters.academic.gpaMax}
                  onChange={handleMaxInputChange(
                    "academic",
                    "gpaMin",
                    "gpaMax",
                    2.0,
                    4.0
                  )}
                  type="number"
                  size="small"
                  sx={{ width: "45%" }}
                  InputProps={{ inputProps: { min: 2.0, max: 4.0, step: 0.1 } }}
                />
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Sport Specific */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="sport-content"
            id="sport-header"
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <SportsSoccerIcon
                sx={{ mr: 1, color: theme.palette.primary.main }}
              />
              <Typography>Sport & Position</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="sport-select-label">Sport</InputLabel>
                <Select
                  labelId="sport-select-label"
                  id="sport-select"
                  value={tempFilters.sportSpecific.sport}
                  label="Sport"
                  onChange={(e) =>
                    handleFilterChange("sportSpecific", "sport", e.target.value)
                  }
                >
                  <MenuItem value="football">Football</MenuItem>
                  <MenuItem value="basketball">Basketball</MenuItem>
                  <MenuItem value="baseball">Baseball</MenuItem>
                  <MenuItem value="track">Track & Field</MenuItem>
                  <MenuItem value="soccer">Soccer</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="position-select-label">Position</InputLabel>
                <Select
                  labelId="position-select-label"
                  id="position-select"
                  value={tempFilters.sportSpecific.position}
                  label="Position"
                  onChange={(e) =>
                    handleFilterChange(
                      "sportSpecific",
                      "position",
                      e.target.value
                    )
                  }
                >
                  {tempFilters.sportSpecific.sport === "football" && (
                    <>
                      <MenuItem value="">Any Position</MenuItem>
                      <MenuItem value="QB">Quarterback (QB)</MenuItem>
                      <MenuItem value="RB">Running Back (RB)</MenuItem>
                      <MenuItem value="WR">Wide Receiver (WR)</MenuItem>
                      <MenuItem value="TE">Tight End (TE)</MenuItem>
                      <MenuItem value="OL">Offensive Line (OL)</MenuItem>
                      <MenuItem value="DL">Defensive Line (DL)</MenuItem>
                      <MenuItem value="LB">Linebacker (LB)</MenuItem>
                      <MenuItem value="CB">Cornerback (CB)</MenuItem>
                      <MenuItem value="S">Safety (S)</MenuItem>
                    </>
                  )}

                  {tempFilters.sportSpecific.sport === "basketball" && (
                    <>
                      <MenuItem value="">Any Position</MenuItem>
                      <MenuItem value="PG">Point Guard (PG)</MenuItem>
                      <MenuItem value="SG">Shooting Guard (SG)</MenuItem>
                      <MenuItem value="SF">Small Forward (SF)</MenuItem>
                      <MenuItem value="PF">Power Forward (PF)</MenuItem>
                      <MenuItem value="C">Center (C)</MenuItem>
                    </>
                  )}

                  {tempFilters.sportSpecific.sport === "baseball" && (
                    <>
                      <MenuItem value="">Any Position</MenuItem>
                      <MenuItem value="P">Pitcher (P)</MenuItem>
                      <MenuItem value="C">Catcher (C)</MenuItem>
                      <MenuItem value="1B">First Base (1B)</MenuItem>
                      <MenuItem value="2B">Second Base (2B)</MenuItem>
                      <MenuItem value="3B">Third Base (3B)</MenuItem>
                      <MenuItem value="SS">Shortstop (SS)</MenuItem>
                      <MenuItem value="OF">Outfield (OF)</MenuItem>
                    </>
                  )}

                  {(tempFilters.sportSpecific.sport === "track" ||
                    tempFilters.sportSpecific.sport === "soccer") && (
                    <MenuItem value="">Any Position</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Location */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="location-content"
            id="location-header"
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <LocationOnIcon
                sx={{ mr: 1, color: theme.palette.primary.main }}
              />
              <Typography>Location</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <LocationFilters
              filters={tempFilters.location}
              onChange={(values) => {
                // Update the location filters in tempFilters
                setTempFilters((prev) => ({
                  ...prev,
                  location: {
                    ...prev.location,
                    ...values,
                  },
                }));
              }}
            />
          </AccordionDetails>
        </Accordion>
      </Box>

      <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button
          variant="contained"
          fullWidth
          onClick={handleApplyFilters}
          sx={{ mb: 1 }}
        >
          Apply Filters
        </Button>
        <Button variant="outlined" fullWidth onClick={onClearAllFilters}>
          Reset All
        </Button>
      </Box>
    </Drawer>
  );
};

export default AdvancedFilterPanel;
